import { useRef, useState } from "react";
import { api, type TripView } from "./api.ts";
import { ConfirmDialog } from "./Dialog.tsx";
import { firstName } from "./faces.ts";
import { lastDay } from "./format.ts";
import { useKeptScroll } from "./kept.ts";
import { DietAndMobility } from "./Needs.tsx";
import { navigate, tripPath } from "./router.ts";
import { DangerAction, SettingsGroup } from "./SettingsGroup.tsx";
import { ShareToView } from "./ShareToView.tsx";
import { PeopleAndSpots } from "./Spots.tsx";
import { TripDetails } from "./TripDetails.tsx";

// Trip settings (in the trip menu, the trip's name), laid out like the profile, raised cards under eyebrow headings
// (DESIGN.md, "Trip settings"): The trip, its name and dates (TripDetails.tsx); People and spots, for everyone (how
// many are used, and for its owner a way to add more); Diet and mobility, everyone's, mine to edit (Needs.tsx: kept in
// the app only, never by or to an AI); and last a Danger zone (DESIGN.md, "A danger zone"): leaving
// the trip, for anyone but its owner, or deleting it, for its owner only. Each asks first, in a dialog saying what
// happens. Deleting is only ever done here: AIs can't (features/trips-and-members.md).

const KEEP_DAYS = 30;

// `onLeaving`: this page is about to leave or delete the trip, so it doesn't stop on hearing it go.
export function TripSettings({
  trip,
  onLeaving,
  onChanged,
}: {
  trip: TripView;
  onLeaving: (leaving: boolean) => void;
  // Something here changed the trip's view (a refund asked for): load it again.
  onChanged: () => void;
}) {
  const owner = trip.owner === trip.you;
  const [asking, setAsking] = useState<"leave" | "delete" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const page = useRef<HTMLElement>(null);
  useKeptScroll(page, "settings");

  const ask = (what: "leave" | "delete") => {
    setError(null);
    setAsking(what);
  };
  const act = async (run: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    onLeaving(true);
    try {
      await run();
    } catch (e) {
      onLeaving(false);
      setError(e instanceof Error ? e.message : "That didn't work. Try again.");
      setBusy(false);
    }
  };
  // Gone from here: Your trips, in this step's place (Back finds the trip saying it's gone).
  const leave = () =>
    act(async () => {
      const me = trip.members.find((m) => m.id === trip.you);
      await api.remove(trip.id, me?.member ?? "");
      navigate("/", { replace: true });
    });
  const remove = () =>
    act(async () => {
      await api.deleteTrip(trip.id);
      navigate("/", { replace: true, state: { deleted: { id: trip.id, title: trip.title } } });
    });

  const others = trip.members.length - 1;
  // Leaving takes my membership away: coming back is asking again with the join link, and the owner letting me in
  // (src/store/store.ts, requestJoin), as anyone new.
  const ownerName = trip.members.find((m) => m.id === trip.owner)?.name;
  const comeBack = `to come back, you'd ask to join again with its link, and ${ownerName ? firstName(ownerName) : "its owner"} would let you in`;
  const until = lastDay(new Date(Date.now() + KEEP_DAYS * 86_400_000).toISOString());
  return (
    <section className="tm-settings" aria-labelledby="settings-title" ref={page}>
      <div className="tm-settings-inner">
        <h1 id="settings-title" className="display-m">
          Trip settings
        </h1>

        <TripDetails trip={trip} onChanged={onChanged} />

        {trip.seats && <PeopleAndSpots trip={trip} seats={trip.seats} onChanged={onChanged} />}

        <DietAndMobility trip={trip} onChanged={onChanged} />

        <ShareToView trip={trip} onChanged={onChanged} />

        <SettingsGroup id="danger-title" title="Danger zone" danger>
          {owner ? (
            <DangerAction
              what={`${others > 0 ? "Deleting the trip takes it away for everyone in it, at once." : "Deleting the trip takes it from your trips."} You can restore it from Your trips for ${KEEP_DAYS} days; after that it's gone for good, with its files.`}
              action="Delete this trip"
              onClick={() => ask("delete")}
            >
              {others > 0 && (
                <p className="caption tm-muted">
                  To leave it instead, offer it to someone on{" "}
                  <a
                    className="tm-link"
                    href={tripPath(trip.id, null, "people")}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                      e.preventDefault();
                      navigate(tripPath(trip.id, null, "people"));
                    }}
                  >
                    People
                  </a>
                  : once they've taken it over, you can leave.
                </p>
              )}
            </DangerAction>
          ) : (
            <DangerAction
              what={`Leaving takes the trip from your trips. What you added stays in it for the others; ${comeBack}.`}
              action="Leave this trip"
              onClick={() => ask("leave")}
            />
          )}
        </SettingsGroup>
      </div>

      {asking === "leave" && (
        <ConfirmDialog
          title={`Leave ${trip.title}?`}
          action="Leave trip"
          doing="Leaving…"
          busy={busy}
          error={error}
          onConfirm={leave}
          onCancel={() => setAsking(null)}
        >
          <p className="body">{`It goes from your trips. What you added stays in it for the others; ${comeBack}.`}</p>
        </ConfirmDialog>
      )}
      {asking === "delete" && (
        <ConfirmDialog
          title={`Delete ${trip.title}?`}
          action="Delete trip"
          doing="Deleting…"
          busy={busy}
          error={error}
          onConfirm={remove}
          onCancel={() => setAsking(null)}
        >
          <p className="body">
            {others > 0
              ? `It disappears now for all ${trip.members.length} people.`
              : "It disappears from your trips now."}
          </p>
          <p className="body">
            You can restore it from Your trips, under Recently deleted, until {until}. After that it's gone for good,
            with its files.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
